import { useState, type FormEvent } from 'react';
import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { DataTable, type Column } from '@/components/DataTable/DataTable';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { TextField } from '@/components/Field/Field';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { SeverityBadge } from '@/features/admin/components/SeverityBadge/SeverityBadge';
import { api } from '@/lib/api/endpoints';
import type { ReadinessFinding, WorkshopSource } from '@/lib/api/types';
import { formatDateTime } from '@/lib/format';
import { useAsync } from '@/lib/hooks/useAsync';
import { useSubmit } from '@/lib/hooks/useSubmit';
import styles from './pages.module.scss';

/**
 * Workshop sources (Epic 2): register a repo+tag, sync it server-side, and read
 * the readiness report.
 *
 * Repository and commit identifiers are visible *here* — this screen is behind
 * the admin claim and the audit trail records who registered what. They must
 * never appear on an attendee surface (NFR-AP1).
 */
export function SourcesPage() {
  const { state, reload } = useAsync(() => api.sources.list().then((result) => result.sources), []);
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className={styles.stack}>
      <PageHeader
        eyebrow="Epic 2"
        title="Workshop sources"
        description="A source is one repository in the Alpantos organisation pinned to one immutable tag. Content is fetched server-side; the repository is never exposed to attendees."
      />

      <RegisterSourceForm onRegistered={reload} />

      <Card title="Registered sources">
        <AsyncBoundary state={state} subject="workshop sources" plannedUnder="US-2.1" onRetry={reload}>
          {(sources) => (
            <SourceTable sources={sources} selected={selected} onSelect={setSelected} />
          )}
        </AsyncBoundary>
      </Card>

      {selected && <ReadinessReport sourceId={selected} />}
    </div>
  );
}

function RegisterSourceForm({ onRegistered }: { onRegistered: () => void }) {
  const [repository, setRepository] = useState('');
  const [tag, setTag] = useState('');
  const { submit, busy, message, status } = useSubmit(api.sources.register, () => {
    setRepository('');
    setTag('');
    onRegistered();
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit({ repository: repository.trim(), tag: tag.trim() });
  };

  return (
    <Card title="Register a source (US-2.1)">
      <form onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          <TextField
            label="Repository"
            required
            placeholder="workshop-repo-name"
            hint="Repository name only. The organisation is fixed server-side to Alpantos."
            value={repository}
            onChange={(event) => setRepository(event.target.value)}
          />
          <TextField
            label="Tag or release"
            required
            placeholder="v1.4.0"
            hint="An exact, immutable tag. The commit SHA behind it is recorded on registration."
            value={tag}
            onChange={(event) => setTag(event.target.value)}
          />
        </div>
        <div className={styles.formActions}>
          {message && <span className={styles.note}>{message}</span>}
          {status === 'done' && <span className={styles.note}>Source registered.</span>}
          <Button type="submit" variant="primary" disabled={busy || !repository || !tag}>
            {busy ? 'Registering…' : 'Register source'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SourceTable({
  sources,
  selected,
  onSelect,
}: {
  sources: readonly WorkshopSource[];
  selected: string | null;
  onSelect: (sourceId: string) => void;
}) {
  const columns: ReadonlyArray<Column<WorkshopSource>> = [
    { key: 'repository', header: 'Repository', render: (source) => source.repository },
    { key: 'tag', header: 'Tag', render: (source) => <code>{source.tag}</code> },
    {
      key: 'commit',
      header: 'Commit',
      secondary: true,
      render: (source) => <code>{source.commit_sha.slice(0, 10)}</code>,
    },
    {
      key: 'registered',
      header: 'Registered',
      secondary: true,
      render: (source) => `${formatDateTime(source.registered_at)} · ${source.registered_by}`,
    },
    {
      key: 'actions',
      header: '',
      render: (source) => (
        <span className={styles.inlineActions}>
          <Button size="sm" onClick={() => void api.sources.sync(source.id)}>
            Sync
          </Button>
          <Button
            size="sm"
            variant={selected === source.id ? 'primary' : 'secondary'}
            onClick={() => onSelect(source.id)}
          >
            Readiness
          </Button>
        </span>
      ),
    },
  ];

  return (
    <DataTable
      caption="Registered workshop sources"
      columns={columns}
      rows={sources}
      rowKey={(source) => source.id}
      empty={
        <EmptyState
          title="No sources registered"
          description="Register a repository and tag above to pull its content in."
        />
      }
    />
  );
}

/** US-2.3 — findings grouped by severity, with the fix for each. */
function ReadinessReport({ sourceId }: { sourceId: string }) {
  const { state, reload } = useAsync(
    () => api.sources.readiness(sourceId).then((result) => result.findings),
    [sourceId],
  );

  const columns: ReadonlyArray<Column<ReadinessFinding>> = [
    {
      key: 'severity',
      header: 'Severity',
      render: (finding) => <SeverityBadge severity={finding.severity} />,
    },
    {
      key: 'where',
      header: 'Where',
      render: (finding) => [finding.module_ref, finding.topic_ref].filter(Boolean).join(' · ') || '—',
    },
    { key: 'message', header: 'Finding', render: (finding) => finding.message },
    {
      key: 'fix',
      header: 'How to fix',
      secondary: true,
      render: (finding) => finding.remediation ?? '—',
    },
  ];

  return (
    <Card
      title="Readiness report (US-2.3)"
      actions={
        <Button size="sm" onClick={reload}>
          Re-run check
        </Button>
      }
      footer="A cohort cannot be published while blocking errors remain."
    >
      <AsyncBoundary state={state} subject="the readiness report" plannedUnder="US-2.3" onRetry={reload}>
        {(findings) => (
          <DataTable
            caption="Readiness findings"
            columns={columns}
            rows={findings}
            rowKey={(finding) => String(finding.id)}
            empty={
              <EmptyState
                title="No findings"
                description="This source passed every readiness check and can be assigned to a cohort."
              />
            }
          />
        )}
      </AsyncBoundary>
    </Card>
  );
}
